import React, { useState, useEffect, useMemo, useRef } from "react";
import { API } from "aws-amplify";
import Fuse from "fuse.js";
import swal from "@sweetalert/with-react";

import BulkEditCourseRequest from "../../BulkEditCourseRequest";
import Toggle from "../../Toggle";
import Loader from "../common/Loader";
import SearchAddButton from "../../SearchAddButton";

import {
  calculateConflicts,
  capitalizeFirstLetters,
  isAlternateCourseRequest,
  loadData,
} from "../../../js/Helpers";
import {
  addStudentRequestToDraft,
  buildRequestSelectionKey,
  getStudentsRequestingCourseWithDetails,
  studentHasActiveCourseRequest,
} from "../utils/courseRequestDraftHelpers";
import {
  saveCourseRequestInclusion,
  setCourseRequestInclusionInStudents,
} from "../utils/courseRequestInclusionHelpers";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckSquare,
  faChevronDown,
  faChevronUp,
  faSearch,
  faSlidersH,
  faSquare,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { faSquare as farSquare } from "@fortawesome/free-regular-svg-icons";
import { useConflictActions } from "../store/magnetboardStore";

export default function StudentRequests({
  schoolInfo,
  scheduleInfo,
  sections,
  setSections,
  students,
  setStudents,
  courses,
  courseID,
  draftMode = false,
}) {
  const [gradeLevels, setGradeLevels] = useState([]);
  const [gradesSelected, setGradesSelected] = useState([]);
  const [studentsByGradeSelected, setStudentsByGradeSelected] = useState([]);
  const [showOnlyStudentsWithoutRequest, setShowOnlyStudentsWithoutRequest] =
    useState(false);
  const [filterExistingStudentIDs, setFilterExistingStudentIDs] = useState([]);
  const [filterIDs, setFilterIDs] = useState([]);
  const [currentRequestsSearchValue, setCurrentRequestsSearchValue] = useState("");
  const [currentRequestsFiltersVisible, setCurrentRequestsFiltersVisible] =
    useState(false);
  const [currentRequestsOpenFilters, setCurrentRequestsOpenFilters] = useState([]);
  const [currentRequestsFilters, setCurrentRequestsFilters] = useState({
    grade: [],
    alternates: [],
  });
  const currentRequestsFilterRef = useRef();

  const [selectedStudents, setSelectedStudents] = useState([]);
  const [addingStudentRequestIDs, setAddingStudentRequestIDs] = useState([]);
  const [addingGradeRequests, setAddingGradeRequests] = useState(false);
  const [gradeRequestsSuccessMessage, setGradeRequestsSuccessMessage] =
    useState("");
  const [requestsSaving, setRequestsSaving] = useState(false);
  const [inclusionRequestsSaving, setInclusionRequestsSaving] = useState([]);
  const { setConflicts, setMatchPercent, markConflictDataFresh } = useConflictActions();

  /////////////////////
  ///// FUNCTIONS /////
  /////////////////////
  const fuzzySearch = (e, search_type) => {
    const search_value = e.target.value;

    const filtered_student_ids = search_value.trim().length === 0
      ? students.map((student) => student.student_id)
      : [];

    if (search_value.trim().length === 0) {
      if (search_type === "existing")
        setFilterExistingStudentIDs([...filtered_student_ids]);
      if (search_type === "all") setFilterIDs([...filtered_student_ids]);
      return;
    }

    // Search courses by name or code
    const students_fuse = new Fuse(students, {
      keys: ["last_name", "first_name"],
      threshold: 0.1,
    });

    const student_results = students_fuse.search(search_value);
    const subset_student_results = student_results
      .map((result) => result.item)
      .slice(0, 4);

    subset_student_results.forEach((student) => {
      const student_id = student.student_id;

      filtered_student_ids.push(student_id);
    });

    if (search_type === "existing")
      setFilterExistingStudentIDs([...filtered_student_ids]);
    if (search_type === "all") setFilterIDs([...filtered_student_ids]);
  };

  const toggleCurrentRequestsFilterPanel = () => {
    setCurrentRequestsFiltersVisible(
      (current_filter_visible) => !current_filter_visible,
    );
  };

  const toggleCurrentRequestsOpenFilter = (filter_title) => {
    setCurrentRequestsOpenFilters((current_open_filters) =>
      current_open_filters.includes(filter_title)
        ? current_open_filters.filter((current_filter) => current_filter !== filter_title)
        : [...current_open_filters, filter_title],
    );
  };

  const updateCurrentRequestsFilter = (filter_title, filter_value, selected) => {
    setCurrentRequestsFilters((current_filters) => {
      const current_filter_values = current_filters[filter_title] || [];
      const normalized_filter_value = `${filter_value}`;

      return {
        ...current_filters,
        [filter_title]: selected
          ? [...new Set([...current_filter_values, normalized_filter_value])]
          : current_filter_values.filter(
              (current_filter_value) => current_filter_value !== normalized_filter_value,
            ),
      };
    });
  };

  const toggleSelectStudent = (composite_key) => {
    setSelectedStudents((current_selected_students) => {
      const student_selected_index = current_selected_students.findIndex(
        (key) => key === composite_key,
      );

      if (student_selected_index !== -1) {
        return current_selected_students.filter((key) => key !== composite_key);
      }

      return [...current_selected_students, composite_key];
    });
  };

  const toggleGradeSelected = (grade) => {
    setGradeRequestsSuccessMessage("");
    const grade_currently_selected_index = gradesSelected.findIndex(
      (selected_grade) => selected_grade === grade,
    );
    let new_students_by_grade_selected = [];

    if (grade_currently_selected_index !== -1) {
      new_students_by_grade_selected = studentsByGradeSelected.reduce(
        (results, student_id) => {
          const student_info = students.find(
            (student) => student.student_id === student_id,
          );
          const student_grade = student_info.grade;

          if (student_grade !== grade) results.push(student_id);
          return results;
        },
        [],
      );

      gradesSelected.splice(grade_currently_selected_index, 1);
    } else {
      new_students_by_grade_selected = students.reduce(
        (results, student_info) => {
          const student_id = student_info.student_id;
          const student_grade = student_info.grade;

          if (
            !studentsByGradeSelected.includes(student_id) &&
            student_grade === grade
          )
            results.push(student_id);
          return results;
        },
        studentsByGradeSelected,
      );

      gradesSelected.push(grade);
    }

    setStudentsByGradeSelected([...new_students_by_grade_selected]);
    setGradesSelected([...gradesSelected]);
  };

  const reloadRequestData = async ({ includeSections = false } = {}) => {
    const data_types = includeSections ? ["students", "sections"] : ["students"];

    const loaded_data = await loadData({
      school_id: schoolInfo.school_id,
      schedule_version_id: schoolInfo.current_schedule_version_id,
      data_version_id: schoolInfo.current_data_version_id,
      data_types,
    });

    if (loaded_data && loaded_data.students) {
      setStudents(loaded_data.students);
    }

    if (includeSections && loaded_data && loaded_data.sections) {
      setSections(loaded_data.sections);
    }
  };

  const addRequest = async (
    student_ids,
    { loadingStudentID = null, isGradeBulk = false } = {},
  ) => {
    if (draftMode) {
      const unique_student_ids = Array.from(new Set(student_ids));
      if (unique_student_ids.length === 0) return;

      setStudents((current_students) =>
        unique_student_ids.reduce(
          (next_students, student_id) =>
            addStudentRequestToDraft(next_students, student_id, courseID),
          current_students,
        ),
      );
      setSelectedStudents([]);

      if (isGradeBulk) {
        setGradeRequestsSuccessMessage(
          `${unique_student_ids.length} draft request${unique_student_ids.length === 1 ? "" : "s"} added for ${capitalizeFirstLetters(course_info?.name || "this course")}!`,
        );
      }

      return;
    }

    if (requestsSaving) return;

    const unique_student_ids = Array.from(new Set(student_ids));
    if (unique_student_ids.length === 0) return;

    if (loadingStudentID) {
      setAddingStudentRequestIDs((current_ids) => [
        ...new Set([...current_ids, loadingStudentID]),
      ]);
    }

    if (isGradeBulk) {
      setAddingGradeRequests(true);
      setGradeRequestsSuccessMessage("");
    }

    setRequestsSaving(true);

    // Let React paint the loading state before the save work starts.
    await new Promise((resolve) => setTimeout(resolve, 0));
    const passed_data = {
      school_id: schoolInfo.school_id,
      schedule_version_id: schoolInfo.current_schedule_version_id,
      update_type: "add",
      course_id: courseID,
      student_ids: unique_student_ids,
    };
    const apiName = process.env.REACT_APP_ENDPOINT_NAME;
    const url = "/admin/update-requests";
    const myInit = {
      response: true,
      body: JSON.stringify(passed_data),
    };

    try {
      await API.post(apiName, url, myInit);

      await reloadRequestData();
      setSelectedStudents([]);

      calculateConflicts({
        school_id: schoolInfo.school_id,
        schedule_version_id: schoolInfo.current_schedule_version_id,
        match_percent: true,
      }).then((conflicts_response) => {
        const conflicts_list = Array.isArray(conflicts_response?.conflicts)
          ? conflicts_response.conflicts
          : [];
        setConflicts([...conflicts_list]);
        setMatchPercent(conflicts_response?.match_percent);
        markConflictDataFresh(schoolInfo.school_id, schoolInfo.current_schedule_version_id, {complete: true});
      });

      if (isGradeBulk) {
        setGradeRequestsSuccessMessage(
          `${unique_student_ids.length} requests have been added for ${capitalizeFirstLetters(course_info.name)}!`,
        );
      }
    } catch (e) {
      console.log(e);
    } finally {
      setRequestsSaving(false);

      if (loadingStudentID) {
        setAddingStudentRequestIDs((current_ids) =>
          current_ids.filter((student_id) => student_id !== loadingStudentID),
        );
      }

      if (isGradeBulk) {
        setAddingGradeRequests(false);
      }
    }
  };

  const updateRequestInclusion = async (student_entry, next_state) => {
    if(`${scheduleInfo?.separate_ICR_codes}` !== "0") return;

    const selection_key = buildRequestSelectionKey(student_entry.group_id, student_entry.student_id);
    const saving_key = `${selection_key}:${courseID}`;

    if(draftMode)
    {
      setStudents((current_students) => setCourseRequestInclusionInStudents(current_students, selection_key, courseID, next_state));
      return;
    }

    if(inclusionRequestsSaving.includes(saving_key)) return;
    setInclusionRequestsSaving((current_keys) => [...current_keys, saving_key]);
    setStudents((current_students) => setCourseRequestInclusionInStudents(current_students, selection_key, courseID, next_state));

    try {
      await saveCourseRequestInclusion({
        schoolInfo,
        selectionKey: selection_key,
        courseID,
        isInclusion: next_state,
      });
      calculateConflicts({
        school_id: schoolInfo.school_id,
        schedule_version_id: schoolInfo.current_schedule_version_id,
        match_percent: true,
      }).then((conflicts_response) => {
        setConflicts(Array.isArray(conflicts_response?.conflicts) ? conflicts_response.conflicts : []);
        setMatchPercent(conflicts_response?.match_percent);
        markConflictDataFresh(schoolInfo.school_id, schoolInfo.current_schedule_version_id, {complete: true});
      });
    } catch(error) {
      console.log(error);
      setStudents((current_students) => setCourseRequestInclusionInStudents(current_students, selection_key, courseID, !next_state));
      swal({
        title: "Inclusion Request Not Updated",
        text: "The request could not be updated. Please try again.",
        icon: "warning",
      });
    } finally {
      setInclusionRequestsSaving((current_keys) => current_keys.filter((current_key) => current_key !== saving_key));
    }
  };

  //// Set Grade Levels ////
  useEffect(() => {
    if (gradeLevels.length > 0) return;

    const grades = students.reduce((result, student) => {
      const student_grade = student.grade;
      if (result.findIndex((option) => option === student_grade) === -1)
        result.push(student_grade);
      return result;
    }, []);

    grades.sort(function (a, b) {
      return a - b;
    }); // sort array numerically
    setGradeLevels(grades);
  }, [students, gradeLevels]);

  useEffect(() => {
    const filtered_student_ids = [];
    students.forEach((student) => {
      const student_id = student.student_id;

      filtered_student_ids.push(student_id);
    });

    setFilterIDs([...filtered_student_ids]);
  }, [students]);

  useEffect(() => {
    if (!currentRequestsFiltersVisible) return;

    const owner_document = currentRequestsFilterRef.current?.ownerDocument || document;
    const handleClickOutside = (event) => {
      if (currentRequestsFilterRef.current?.contains(event.target)) return;
      setCurrentRequestsFiltersVisible(false);
    };

    owner_document.addEventListener("mousedown", handleClickOutside);
    owner_document.addEventListener("touchstart", handleClickOutside);

    return () => {
      owner_document.removeEventListener("mousedown", handleClickOutside);
      owner_document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [currentRequestsFiltersVisible]);

  useEffect(() => {
    if (currentRequestsFiltersVisible) return;
    setCurrentRequestsOpenFilters([]);
  }, [currentRequestsFiltersVisible]);

  const course_info = courses.find((course) => `${course.course_id}` === `${courseID}`);
  const sorted_students_requesting_course = getStudentsRequestingCourseWithDetails(
    students,
    courseID,
  );

  const request_type_count = sorted_students_requesting_course.reduce(
    (results, student_entry) => {
      const student_id = student_entry.student_id;
      const request_info = student_entry.request_info;

      const is_alternate = student_entry.is_primary_request === false;
      const is_inclusion = request_info.is_inclusion === "1";

      if (!is_alternate) results.primary_requests.push(student_id);
      if (is_alternate) results.alternate_requests.push(student_id);
      if (is_inclusion) results.inclusion_requests.push(student_id);

      return results;
    },
    { primary_requests: [], alternate_requests: [], inclusion_requests: [] },
  );

  const currentRequestFilterOptions = useMemo(
    () => ({
      grade: gradeLevels.map((grade) => ({
        display: `${grade}th grade`,
        value: `${grade}`,
      })),
      alternates: [
        { display: "Primary", value: "non-alternate" },
        { display: "Alternates", value: "alternate" },
      ],
    }),
    [gradeLevels],
  );

  const selectedCurrentRequestFilters = useMemo(
    () =>
      Object.keys(currentRequestsFilters).reduce((results, filter_title) => {
        const filter_options = currentRequestFilterOptions[filter_title] || [];
        const selected_values = currentRequestsFilters[filter_title] || [];

        selected_values.forEach((selected_value) => {
          const filter_option = filter_options.find(
            (option) => `${option.value}` === `${selected_value}`,
          );
          if (filter_option) {
            results.push({
              filter_title,
              value: selected_value,
              display: filter_option.display,
            });
          }
        });

        return results;
      }, []),
    [currentRequestFilterOptions, currentRequestsFilters],
  );

  const filteredStudentsRequestingCourse = useMemo(() => {
    const normalized_search_value = currentRequestsSearchValue.trim().toLowerCase();
    const selected_grade_values = new Set(currentRequestsFilters.grade || []);
    const selected_alternate_values = new Set(
      currentRequestsFilters.alternates || [],
    );

    return sorted_students_requesting_course.filter((student_entry) => {
      const student_grade = `${student_entry.grade}`;
      const request_info = student_entry.request_info || {};
      const request_is_alternate = isAlternateCourseRequest(
        request_info,
        student_entry.request_group,
      );

      if (
        selected_grade_values.size > 0 &&
        !selected_grade_values.has(student_grade)
      ) {
        return false;
      }

      if (selected_alternate_values.size > 0) {
        const alternate_filter_match =
          (request_is_alternate && selected_alternate_values.has("alternate")) ||
          (!request_is_alternate &&
            selected_alternate_values.has("non-alternate"));
        if (!alternate_filter_match) return false;
      }

      if (normalized_search_value.length > 0) {
        const search_text = `${student_entry.last_name || ""} ${student_entry.first_name || ""}`.toLowerCase();
        return search_text.includes(normalized_search_value);
      }

      return true;
    });
  }, [
    currentRequestsFilters,
    currentRequestsSearchValue,
    sorted_students_requesting_course,
  ]);

  const filteredCurrentRequestStudentCount = useMemo(
    () =>
      new Set(
        filteredStudentsRequestingCourse.map((student_entry) =>
          `${student_entry.student_id}`,
        ),
      ).size,
    [filteredStudentsRequestingCourse],
  );

  const currentRequestsListFiltered =
    currentRequestsSearchValue.trim().length > 0 ||
    selectedCurrentRequestFilters.length > 0;

  const filteredCurrentRequestSelectionKeys = useMemo(
    () =>
      filteredStudentsRequestingCourse.map((student_entry) =>
        buildRequestSelectionKey(student_entry.group_id, student_entry.student_id),
      ),
    [filteredStudentsRequestingCourse],
  );

  return (
    <>
      <h4>Student Requests</h4>
      <h1 className="report-name">
        {capitalizeFirstLetters(course_info?.name || "Unknown Course")} ({course_info?.course_code || "--"})
      </h1>
      <div className="student-requests-course-container">
        <div className="student-requests-add-request-container">
          <div className="student-requests-subheader-container">
            <h2 style={{ margin: "0px" }}>Add Requests</h2>
          </div>
          <h3>By student:</h3>
          <div className="search-add-adding-input-container">
            <input
              id="main-students-input"
              className="search-add-adding-input"
              onChange={(e) => fuzzySearch(e, "all")}
              placeholder="Search for student..."
            />
            <FontAwesomeIcon
              className="search-add-adding-input-icon"
              icon={faSearch}
              autoFocus={true}
            />
          </div>
          <div
            id="student-requests-filter-students-with-request"
            onClick={() =>
              setShowOnlyStudentsWithoutRequest(!showOnlyStudentsWithoutRequest)
            }
          >
            <FontAwesomeIcon
              icon={showOnlyStudentsWithoutRequest ? faCheckSquare : faSquare}
              className={
                showOnlyStudentsWithoutRequest
                  ? "fas-checkbox-checked"
                  : "fas-checkbox-unchecked"
              }
            />
            <div>Only show students without this course request</div>
          </div>
          <div
            className="search-add-search-results"
            style={{ maxHeight: "400px" }}
          >
            {students.map((student, i) => {
              const student_id = student.student_id;
              const student_add_loading =
                addingStudentRequestIDs.includes(student_id);
              if (!filterIDs.includes(student_id)) return null;

              const student_already_requesting = studentHasActiveCourseRequest(
                student,
                courseID,
                { isInclusion: false },
              );
              if (
                showOnlyStudentsWithoutRequest &&
                student_already_requesting &&
                !student_add_loading
              )
                return null;
              return (
                <div
                  className="search-add-search-result student-requests-report-search-result"
                  key={i}
                >
                  <div>
                    <div>
                      {student.last_name}, {student.first_name}
                    </div>
                    <div className="small-text dark-gray-text">
                      {student.grade}th grade
                    </div>
                  </div>
                  <SearchAddButton
                    selected={student_already_requesting}
                    loading={student_add_loading}
                    onAdd={() =>
                      addRequest([student_id], {
                        loadingStudentID: student_id,
                      })
                    }
                    label="Add Request"
                  />
                </div>
              );
            })}
          </div>
          <h3>By grade:</h3>
          <div>
            {gradeLevels.map((grade) => {
              const grade_selected = gradesSelected.includes(grade);

              return (
                <div
                  className="student-requests-grade-row"
                  key={grade}
                  onClick={() => toggleGradeSelected(grade)}
                >
                  <FontAwesomeIcon
                    icon={grade_selected ? faCheckSquare : faSquare}
                    className={
                      grade_selected
                        ? "fas-checkbox-checked"
                        : "fas-checkbox-unchecked"
                    }
                  />
                  <div>{grade}</div>
                </div>
              );
            })}
            {studentsByGradeSelected.length > 0 && (
              <>
                <div className="student-requests-add-by-grade-student-list">
                  {studentsByGradeSelected.map((student_id, j) => {
                    const student_info = students.find(
                      (student) => student.student_id === student_id,
                    );

                    if (
                      filterExistingStudentIDs.length > 0 &&
                      !filterExistingStudentIDs.includes(student_id)
                    )
                      return null;

                    const student_first_name = student_info
                      ? student_info.first_name
                      : null;
                    const student_last_name = student_info
                      ? student_info.last_name
                      : null;
                    const student_grade = student_info
                      ? student_info.grade
                      : null;

                    return (
                      <div className="student-requests-request-line" key={j}>
                        <div>
                          <div>
                            {student_last_name}, {student_first_name}
                          </div>
                          <div className="small-text dark-gray-text">
                            {student_grade ? `${student_grade}th grade` : ""}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div
                  className={`btn blue-btn student-requests-grade-submit-btn ${addingGradeRequests ? "student-requests-grade-submit-btn-loading" : ""}`}
                  onClick={() =>
                    addingGradeRequests
                      ? null
                      : addRequest(studentsByGradeSelected, {
                          isGradeBulk: true,
                        })
                  }
                >
                  {addingGradeRequests ? (
                    <Loader size="22px" />
                  ) : (
                    `Add ${studentsByGradeSelected.length} Student Requests`
                  )}
                </div>
                {gradeRequestsSuccessMessage && (
                  <div className="student-requests-grade-success-message green-text">
                    {gradeRequestsSuccessMessage}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <div className="student-requests-current-requests-container">
          <div className="student-requests-subheader-container">
            <h2 style={{ margin: "0px" }}>Current Requests</h2>
          </div>
          <div
            className="reports-search-bar-container student-requests-current-search-container"
            ref={currentRequestsFilterRef}
          >
            <input
              className="reports-search-bar"
              placeholder={`Search for a student...`}
              value={currentRequestsSearchValue}
              onChange={(e) => setCurrentRequestsSearchValue(e.target.value)}
            />
            <FontAwesomeIcon
              className="reports-search-bar-icon"
              icon={faSearch}
            />
            <div className="mb-search-bar-filter-container">
              <div
                className="student-requests-current-filter-btn"
                onClick={toggleCurrentRequestsFilterPanel}
              >
                <FontAwesomeIcon icon={faSlidersH} />
              </div>
              {currentRequestsFiltersVisible && (
                <div
                  className="list-filter-options-container click-restricted student-requests-current-filter-dropdown"
                >
                  <div className="small-text">Filter by:</div>
                  {Object.keys(currentRequestFilterOptions).map((filter_title) => {
                    const filter_options =
                      currentRequestFilterOptions[filter_title] || [];
                    const filter_options_open =
                      currentRequestsOpenFilters.includes(filter_title);
                    const selected_filter_values =
                      currentRequestsFilters[filter_title] || [];
                    const filter_group_selected_count =
                      selected_filter_values.length;
                    const formatted_filter_title =
                      filter_title === "grade" ? "Grade" : "Alternates";

                    return (
                      <div key={`current-requests-filter-${filter_title}`}>
                        <div
                          className="list-filter-title-container"
                          onClick={() => toggleCurrentRequestsOpenFilter(filter_title)}
                        >
                          <div className="list-filter-title-label">
                            {formatted_filter_title}
                            {filter_group_selected_count > 0 && (
                              <span className="list-filter-title-selected-indicator">{filter_group_selected_count}</span>
                            )}
                          </div>
                          <FontAwesomeIcon
                            className="small-text"
                            icon={
                              filter_options_open ? faChevronUp : faChevronDown
                            }
                          />
                        </div>
                        {filter_options_open &&
                          filter_options.map((filter_option) => {
                            const filter_selected =
                              selected_filter_values.includes(
                                `${filter_option.value}`,
                              );

                            return (
                              <div
                                className="parent-div list-filter-option click-restricted"
                                key={`current-requests-filter-${filter_title}-${filter_option.value}`}
                                onClick={() =>
                                  updateCurrentRequestsFilter(
                                    filter_title,
                                    filter_option.value,
                                    !filter_selected,
                                  )
                                }
                              >
                                <div className="list-filter-option-name click-restricted">
                                  {filter_option.display}
                                </div>
                                {filter_selected ? (
                                  <FontAwesomeIcon
                                    className="fas-checkbox-checked click-restricted"
                                    icon={faCheckSquare}
                                  />
                                ) : (
                                  <FontAwesomeIcon
                                    className="fas-checkbox-unchecked click-restricted"
                                    icon={faSquare}
                                  />
                                )}
                              </div>
                            );
                          })}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          {selectedCurrentRequestFilters.length > 0 && (
            <div className="mb-selected-filters-bar student-requests-current-selected-filters-bar">
              <div className="mb-selected-filters-text">Filters:</div>
              {selectedCurrentRequestFilters.map((filter_option) => (
                <div
                  className="mb-selected-filters-filter-tag"
                  key={`current-request-selected-${filter_option.filter_title}-${filter_option.value}`}
                >
                  <div className="mb-selected-filters-filter-tag-inner-container">
                    <div>{filter_option.display}</div>
                    <FontAwesomeIcon
                      className="dark-gray-link"
                      icon={faTimes}
                      onClick={() =>
                        updateCurrentRequestsFilter(
                          filter_option.filter_title,
                          filter_option.value,
                          false,
                        )
                      }
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
          {sorted_students_requesting_course.length > 0 && (
            <div className="student-requests-bulk-edit-container">
              <BulkEditCourseRequest
                courseID={courseID}
                schoolInfo={schoolInfo}
                scheduleInfo={scheduleInfo}
                courses={courses}
                students={students}
                setStudents={setStudents}
                sections={sections}
                setSections={setSections}
                selectedStudents={selectedStudents}
                setSelectedStudents={setSelectedStudents}
                availableSelectionKeys={filteredCurrentRequestSelectionKeys}
                draftMode={draftMode}
              />
            </div>
          )}
          <div>
            {sorted_students_requesting_course.length === 0 ? (
              <div className="conflict-matrix-no-data">No current requests</div>
            ) : (
              <>
                <div className="modal-example-list">
                  {filteredStudentsRequestingCourse.length === 0 ? (
                    <div className="student-requests-empty-list-message">
                      <FontAwesomeIcon icon={faSearch} />
                      <div>No results found</div>
                    </div>
                  ) : filteredStudentsRequestingCourse.map((student_entry, j) => {
                    const student_info = student_entry;
                    const request_info = student_entry.request_info;
                    const group_id = student_entry.group_id;

                    const student_id = student_info.student_id;

                    const student_first_name = student_info.first_name;
                    const student_last_name = student_info.last_name;

                    const request_group = student_entry.request_group;
                    const student_request_is_alternate =
                      isAlternateCourseRequest(request_info, request_group);
                    const student_request_is_lower_order_alternate =
                      `${request_info.order}` !== "1";
                    const student_request_is_inclusion =
                      request_info.is_inclusion === "1";

                    const composite_key = buildRequestSelectionKey(
                      group_id,
                      student_id,
                    );
                    const student_selected =
                      selectedStudents.includes(composite_key);
                    const inclusion_saving_key = `${composite_key}:${courseID}`;
                    const inclusion_request_saving = inclusionRequestsSaving.includes(inclusion_saving_key);

                    let primary_course_name = "";
                    if (student_request_is_lower_order_alternate) {
                      const primary_course_id = request_group?.course_requests
                        ?.find((course_req) => `${course_req.order}` === "1")
                        ?.course_id;
                      if (primary_course_id) {
                        const primary_course_info = courses.find(
                          (course) =>
                            `${course.course_id}` === `${primary_course_id}`
                        );
                        primary_course_name = primary_course_info
                          ? `${primary_course_info.name} (${primary_course_info.course_code})`
                          : "Unknown Course";
                      }
                    }
                    return (
                      <div className={`student-requests-request-line ${`${scheduleInfo?.separate_ICR_codes}` === "0" ? "student-requests-request-line-with-inclusion" : ""}`} key={j}>
                        <FontAwesomeIcon
                          className={
                            student_selected ? "blue-text" : "dark-gray-text"
                          }
                          icon={student_selected ? faCheckSquare : farSquare}
                          onClick={() => toggleSelectStudent(composite_key)}
                        />
                        <div>
                          <div>
                            {student_last_name}, {student_first_name}
                            {student_request_is_alternate && (
                              <span className="small-text">
                                {" - "}
                                <span className="purple-text">Alternate</span>
                                {primary_course_name && (
                                  <>{` for `}<b>{primary_course_name}</b></>
                                )}
                              </span>
                            )}
                            {student_request_is_inclusion && (
                              <span className="small-text orange-text">
                                - Inclusion{" "}
                              </span>
                            )}
                          </div>
                          <div className="small-text dark-gray-text">
                            {student_info.grade}th grade
                          </div>
                        </div>
                        {`${scheduleInfo?.separate_ICR_codes}` === "0" &&
                          <div className="course-request-inclusion-toggle">
                            <span>Inclusion</span>
                            <Toggle
                              toggleOn={student_request_is_inclusion}
                              handleChange={(next_state) => updateRequestInclusion(student_entry, next_state)}
                              isSmall={true}
                              toggleColor="yellow"
                              disabled={inclusion_request_saving}
                            />
                          </div>
                        }
                      </div>
                    );
                  })}
                </div>
                <div className="student-requests-current-request-count">
                  {currentRequestsListFiltered && (
                    <div>
                      <span className="student-requests-current-request-count-label">
                        Shown students
                      </span>
                      <span>{filteredCurrentRequestStudentCount}</span>
                    </div>
                  )}
                  <div>
                    {request_type_count.primary_requests.length} primary
                  </div>
                  {request_type_count.alternate_requests.length > 0 && (
                    <div className="purple-text">
                      {request_type_count.alternate_requests.length} alternates
                    </div>
                  )}
                  {request_type_count.inclusion_requests.length > 0 && (
                    <div className="orange-text">
                      {request_type_count.inclusion_requests.length} inclusion
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
